import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

import simplify from 'simplify-js';

import debounce from 'shared-utils/src/debounce';
import sleep from 'shared-utils/src/sleep';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';

import Draw from 'shared-vectors/icons/MapDraw';
import { Icon } from 'shared-vectors';

import {
  polylineStyle,
  polygonStyle,
  DRAWING_ACTIONS_MAP,
} from '../../../constants';

import {
  getHighestPoint,
  getLowestPoint,
  pxToLatLng,
  latLngToPx,
  mouseCoordsToLatLng,
  setMarkerContent,
  getBounds,
  getPolygonBounds,
  getBboxPolygon,
} from '../../../helpers/gmaps';

import {
  validatePolygonGeometry,
} from '../../../helpers/polygonValidator';

import removeActiveShapes from '../../../helpers/removeActiveShapes';

import Overlayer from '../../layout/Overlayer';
import Controls from './Controls';

const initialIsDrawingCondition = ({ qbGoToSRP = null, drawingData = null }) => {
  if (qbGoToSRP !== null) {
    return false;
  }
  return !drawingData || drawingData.action !== DRAWING_ACTIONS_MAP.polygon;
};

const PolygonTool = ({
  initialized,
  map,
  container,
  libraries,
  drawingData,
  searchMode,
  setSearchMode,
  device,
  getAvailableResults,
  setDrawingData,
  setGeoFilters,
  columnsAreCollapsed,
  updateQuery,
  openQbFiltersPanel,
  qbGoToSRP,
  qbSSearchAction,
  controlsVariant,
  setSaveSearchDrawingData,
  isSafari,
  showYouGotAwayMsg,
  setShowYouGotAwayMsg,
  filters,
  getDrawingData,
  synchPreviousStepsActiveShape,
  setInitialShapeForPrevious,
  deleteDraftAndStopSearchMode,
  toggleDeleteDraftAndStopSearchMode,
  setShapeHasBeenDeleted,
  totalListResults,
  isFetching,
}) => {
  const [isDrawing, setIsDrawing] = useState(initialIsDrawingCondition({ qbGoToSRP, drawingData })); 
  const [hasDrawing, setHasDrawing] = useState(drawingData && drawingData.action === DRAWING_ACTIONS_MAP.polygon);

  const [availableResults, setAvailableResults] = useState(drawingData?.availableResults || { loading: false, value: 0 });
  const [availablePoi, setAvailablePoi] = useState(null);
  const [polygonPath, setPolygonPath] = useState(drawingData?.polygon || null);

  const [pathFromInit] = useState(drawingData?.polygon || null);

  const polylineRef = useRef(null);
  const polygonRef = useRef(null);
  const polygonIsNotValidRef = useRef(false);

  const customVertexes = useRef(null);
  const startDrawingMarker = useRef(null);
  const northernMostVertexRef = useRef(null);
  const deleteMarkerRef = useRef(null);
  const undoMarkerRef = useRef(null);
  const editMarkerRef = useRef(null);
  const centerMsgMarker = useRef(null);

  const isMouseDown = useRef(false);
  const hasDraggedWhileDrawing = useRef(false);
  const lastStateRef = useRef(null);
  const availableResultsRef = useRef(availableResults);
  const filtersRef = useRef(filters);

  const startEditingRef = useRef(false);

  const filtersToAddToQuery = useRef({});
  const createdFromSSearch = useRef(false);

  const createdFromGAM = useRef(false);

  // creo una referenza interna di searchMode attivo per il listener del deleteCirle
  // che altrimenti non recepisce il possibile cambio di valore della prop
  const searchModeRef = useRef(searchMode);

  const mapBoundsOnCreateShape = useRef(null);

  const updateAvailableResults = (arObj) => {
    availableResultsRef.current = arObj;
    setAvailableResults(arObj);
  };

  const removeCenterMarker = () => {
    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }
  };

  const fetchAR = async (isPolygonValidForFetch = true) => {    
    const pathForFilters = polygonRef.current.getPath().getArray().map((coord) => [coord.lat(), coord.lng()]);
    if (!isPolygonValidForFetch) {
      setAvailableResults({ loading: false, value: 0 });
      setAvailablePoi(null);
      availableResultsRef.current = { loading: false, value: 0 };
      removeCenterMarker();
      polygonIsNotValidRef.current = true;
    } else {   
      setAvailableResults({ loading: true, value: 0 });   
      (centerMsgMarker.current || {}).content = setMarkerContent({ poly: true, centerMsg: true, animated: true });
      const ar = await getAvailableResults({
        newLocations: [],
        newFilters: {
          ...filtersRef.current,
          geopolygon: {
            polygon: pathForFilters,
          },
          q: null,
          geobounds: null,
          geocircle: null,
          qExpansion: null,
        },
      });      
      setAvailableResults({ loading: false, value: ar.total });
      setAvailablePoi(ar.availablePoi);
      availableResultsRef.current = { loading: false, value: ar.total };
      (centerMsgMarker.current || {}).content = setMarkerContent({ poly: true, centerMsg: `${ar.total} risultati` });
      polygonIsNotValidRef.current = false;
    }
    setPolygonPath(pathForFilters);
  };

  const deleteDraft = () => {
    if (polylineRef.current) {
      // cancelliamo la polyline
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    if (polygonRef.current) {
      // cancelliamo il poligono
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    // eliminiamo lo startDrawingMarker
    if (startDrawingMarker.current) {
      startDrawingMarker.current.setMap(null);
      startDrawingMarker.current = null;
    }

    if (deleteMarkerRef.current) {
      // cancelliamo il deleteMarkerRef
      deleteMarkerRef.current.setMap(null);
      deleteMarkerRef.current = null;
    }

    // cancelliamo i vertici custom
    if (customVertexes.current) {
      customVertexes.current.map((v) => {
        v.setMap(null);
        v = null;
      });
    }

    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }
  };

  const deletePolygon = () => {
    // cancelliamo la polyline
    polylineRef.current.setMap(null);
    polylineRef.current = null;

    // cancelliamo il poligono
    polygonRef.current.setMap(null);
    polygonRef.current = null;

    // cancelliamo i vertici custom
    if (customVertexes.current) {
      customVertexes.current.map((v) => {
        v.setMap(null);
        v = null;
      });
    }

    // cancelliamo l'editMarker
    if (editMarkerRef.current) {
      editMarkerRef.current.setMap(null);
      editMarkerRef.current = null;
    }          
    // resettiamo il più a nord
    northernMostVertexRef.current = null;
    
    // se esiste cancelliamo l'undoMarker
    if (undoMarkerRef.current) {
      undoMarkerRef.current.setMap(null);
      undoMarkerRef.current = null;
    }
    // cancelliamo il deleteMarkerRef
    deleteMarkerRef.current.setMap(null);
    deleteMarkerRef.current = null;

    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }

    setHasDrawing(false);
    if (qbGoToSRP === null) {
      setIsDrawing(true);
    }

    // se non siamo in fase di disegno cancelliamo i drawing data e facciamo partire la query
    if (!searchModeRef.current) {
      const action = 'delete';
      const deleteShapedata = { ...getDrawingData(), fromBack: true };
      
      setDrawingData(null);
      if (drawingData && drawingData.action === DRAWING_ACTIONS_MAP.polygon && drawingData.fromSSearch) {
        setSaveSearchDrawingData(null);
      }

      const newBounds = map.getBounds();

      updateQuery({
        geobounds: {
          bbox: getBounds(newBounds),
        },
        bboxPolygon: getBboxPolygon(newBounds),
        geopolygon: null,
        q: null,
      }, action, deleteShapedata);

      setShapeHasBeenDeleted(true);
    }
  };

  const getDeleteMarkerCoords = ({ path }) => {
    // calcoliamo l'indice del vertice più a nord all'interno dell'array di tutti i vertici che compongono il path
    northernMostVertexRef.current = getHighestPoint(polylineRef.current);
    // prendiamo le sue coordinate
    return path[northernMostVertexRef.current];
  };
  
  const createDeleteMarker = ({ coords }) => {
    const { marker: { AdvancedMarkerElement } } = libraries;

    deleteMarkerRef.current = new AdvancedMarkerElement({
      map,
      position: { lat: coords.lat(), lng: coords.lng() },
      content: setMarkerContent({ cancel: true }),
      zIndex: 1,
    });

    // al click sul deleteMarker
    deleteMarkerRef.current.addListener('click', deletePolygon);
  };

  /*
    CREAZIONE DEI MARKERS SUI VERTICI DELLA FIGURA
  */
  const setVertexes = ({ path }) => {
    const { marker: { AdvancedMarkerElement }, geometry: { spherical } } = libraries;

    // se esistono già dei vertici custom, li rimuoviamo
    if (customVertexes.current) {
      customVertexes.current.forEach(x => x.setMap(null));
    }

    // e resettiamo l'array che li definisce
    customVertexes.current = [];
    
    // cicliamo l'array dei vertici della figura disegnata
    path.forEach((coords, index) => {
      // se il vertice corrente corrisponde al vertice più a nord al momento della creazione della figura, gli attacchiamo anche il deleteMarker.
      // a seconda di come l'utente decide di editare la figura il vertice più a nord potrebbe cambiare, ma il deleteMarker rimarrà sempre attaccato allo stesso vertice.
      if (index === northernMostVertexRef.current && !deleteMarkerRef.current) {
        createDeleteMarker({ coords });
      }

      // calcoliamo se il vertice è una vertice secondario: non è il primo e ha indice dispari nell'array dei vertici della figura
      const isSmall = index > 0 && index < path.length - 1 && index % 2 === 1;

      // creaimo il marker
      const vx = new AdvancedMarkerElement({
        map,
        position: { lat: coords.lat(), lng: coords.lng() },
        content: setMarkerContent({ vertex: true, isSmall }),
        zIndex: 1,
        gmpDraggable: true, // deve essere draggabile! durante la fase di editing, la figura 'segue' il drag dei singoli marker 
      });

      // ci segnamo se il marker è un vertice secondario perchè nel caso in cui l'utente lo usi per modificare la figura
      // dovremo fare dei calcoli aggiuntivi
      vx.isSmall = isSmall;

      // se l'utente inizia a draggare il marker
      vx.addListener('dragstart', () => {
        // cancelliamo l'undoMarker se presente
        if (undoMarkerRef.current) {
          undoMarkerRef.current.setMap(null);
          undoMarkerRef.current = null;
        }

        // salviamo il path e la posizione del deleteMarker correnti per preparare l'azione undo eventuale
        lastStateRef.current = {
          path: [...polylineRef.current.getPath().getArray()],
          deleteMarkerPosition: deleteMarkerRef.current.position,
          availableResults: availableResultsRef.current.value,
        };
      });

      // mentre l'utente inizia dragga il marker
      vx.addListener('drag', (e) => {
        // inseriamo un nuovo vertice nel path della figura alla positione corrente del cursore
        const newPath = polylineRef.current.getPath();
        newPath.setAt(index, e.latLng);

        // se il marker è quello corrispondente all'ultimo vertice 
        if (index === path.length - 1) {
          // è necessario aggiornare il path anche in corrispondenza del primo vertice 
          newPath.setAt(0, e.latLng);
          // e muovere il primo vertice stesso
          customVertexes.current[0].position = e.latLng;
          // se il vertice più a nord è il primo (che per questione di zindex non viene mai cliccato)
          if (northernMostVertexRef.current === 0) {
            // aggiorniamo la positione del deleteMarker
            deleteMarkerRef.current.position = e.latLng;
          }
        }

        // se il vertice è quello più a nord 
        if (index === northernMostVertexRef.current) {
          // aggiorniamo la positione del deleteMarker
          deleteMarkerRef.current.position = e.latLng;
        }

        // aggioriamo la figura
        polylineRef.current.setPath(newPath);
        polygonRef.current.setPath(newPath);
      });

      // quando l'utente smette di draggare il marker
      vx.addListener('dragend', () => {
        // se si trattava di un vertice secondario, diventa un vertice principale e dobbiamo aggiungere un 
        // vertice secondario tra il marker draggato e quello che lo precede ed uno tra il marker draggato e quello che lo segue
        if (vx.isSmall) {
          // storiamo le istanze dei vertici coinvolti 
          const dragged = polylineRef.current.getPath().getAt(index);
          const prev = polylineRef.current.getPath().getAt(index - 1);
          const succ = polylineRef.current.getPath().getAt(index + 1);

          // calcoliamo il punto mediano tra il marker draggato e quello precedente
          const addedPrevVertexCoords = spherical.computeOffset(prev,
            spherical.computeDistanceBetween(prev, dragged) / 2,
            spherical.computeHeading(prev, dragged));

          // calcoliamo il punto mediano tra il marker draggato e quello successivo
          const addedSuccVertexCoords = spherical.computeOffset(dragged,
            spherical.computeDistanceBetween(dragged, succ) / 2,
            spherical.computeHeading(dragged, succ));
          
          // creiamo una nuova istanza del path della figura
          const newPath = polylineRef.current.getPath();

          // inseriamo due nuovi vertici 'intorno' al marker draggato
          newPath.insertAt(index, addedPrevVertexCoords);          
          newPath.insertAt(index + 2, addedSuccVertexCoords);

          // se il più a nord viene dopo il marker draggato nell'array dei vertici del path,
          // aggiorniamo il suo indice in modo da mantenere la corrispondenza corretta
          if (northernMostVertexRef.current > index) {
            northernMostVertexRef.current += 2;
          }
          
          // aggiorniamo il path della figura
          polylineRef.current.setPath(newPath);
          polygonRef.current.setPath(newPath);

          // stampiamo i vertici aggiornati
          setVertexes({ path: newPath });
        }

        // stampiamo il marker che permette l'undo della modifica 32px a dx della posizione del marker draggato
        const [x, y] = latLngToPx(vx.position, map);
        const vxCoords = pxToLatLng(x + 32, y, map) || vx.position;
    
        undoMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: { lat: vxCoords.lat(), lng: vxCoords.lng() },
          content: setMarkerContent({ undo: true }),
          zIndex: 1,
        });
    
        // al click sull'undoMarker
        undoMarkerRef.current.addListener('click', () => {
          // riportiamo la figura al path precedente
          polylineRef.current.setPath(lastStateRef.current.path);
          polygonRef.current.setPath(lastStateRef.current.path);      
          
          // riportiamo il deleteMarker alla posizione precedente
          deleteMarkerRef.current.position = lastStateRef.current.deleteMarkerPosition;
          setVertexes({ path: lastStateRef.current.path });
          
          if (centerMsgMarker.current) {
            centerMsgMarker.current.content = setMarkerContent({
              poly: true,
              centerMsg: `${lastStateRef.current.availableResults} risultati`,
              animated: false,
            });
          }

          setAvailableResults({ loading: false, value: lastStateRef.current.availableResults });
    
          // cancelliamo l'undoMarker
          undoMarkerRef.current.setMap(null);
          undoMarkerRef.current = null;
        });

        fetchAR();
      });
      // inseriamo il marker nella lista dei marker attivi
      customVertexes.current.push(vx);
    });
  };

  // creiamo il marker che mostra il numero di risultati disponibili
  const createCenterMsgMarker = ({ polygonCenter, animated = false }) => {
    const { marker: { AdvancedMarkerElement } } = libraries;

    const mrkContent = animated
      ? { poly: true, centerMsg: true, animated: true }
      : { poly: true, centerMsg: `${availableResultsRef.current.value} risultati`, animated: false };

    centerMsgMarker.current = new AdvancedMarkerElement({
      map,
      position: {
        lat: polygonCenter.lat(),
        lng: polygonCenter.lng(),
      },
      content: setMarkerContent(mrkContent),
      zIndex: 502,
    });
    centerMsgMarker.current.addListener('click', async () => {
      const path = polygonRef.current.getPath().getArray().map(coord => [coord.lat(), coord.lng()]);
      if (qbGoToSRP) {
        qbGoToSRP({
          tempLocations: {
            searchLocations: [],
            searchLocationsParent: [],
          },
          shapeFilter: { 
            geopolygon: { 
              polygon: path, 
            },
            q: null,
            geobounds: null,
            geocircle: null,
            nearby: null,
          },
          forceMap: true,
        });
      } else {
        confirmPolygon({ 
          path, 
          ar: availableResultsRef.current,
          clickFrom: 'QB_MapLabel',
        });
      }
    });
  };

  const setEditingMode = () => {
    const { core: { LatLngBounds } } = libraries;

    if (editMarkerRef.current) {
      editMarkerRef.current.setMap(null);
      editMarkerRef.current = null;
    
      // se l'undoMarker è visibile lo cancelliamo
      if (undoMarkerRef.current) {
        undoMarkerRef.current.setMap(null);
        undoMarkerRef.current = null;
      }
      // settiamo lo stile corretto alla polyline
      polylineRef.current.setOptions({ ...polylineStyle.editing });
      polygonRef.current.setOptions({ ...polygonStyle.drawing });

      // stampiamo i vertici che permettono di editare la figura
      setVertexes({ path: polylineRef.current.getPath().getArray() });

      const bounds = getPolygonBounds([polygonRef.current], LatLngBounds);

      createCenterMsgMarker({
        polygonCenter: bounds.getCenter(),
      });

      // comunichiamo che siamo in fase di editing
      startEditingRef.current = true;
      setSearchMode(DRAWING_ACTIONS_MAP.polygon);
    }
  };

  const createEditMarker = () => {
    const { marker: { AdvancedMarkerElement } } = libraries;
    // troviamo il vertice più 'basso' della figura e stampiamo il l'editMarker 
    const lowestVertexCoords = polylineRef.current.getPath().getAt(getLowestPoint(polylineRef.current));
    editMarkerRef.current = new AdvancedMarkerElement({
      map,
      position: { lat: lowestVertexCoords.lat(), lng: lowestVertexCoords.lng() },
      content: setMarkerContent({ edit: true }),
      zIndex: 1,
    });

    // al click sull'editMarker
    editMarkerRef.current.addListener('click', setEditingMode);
  };

  /*
    CONFERMA DEL POLIGONO ED USCITA DALLA FASE DI EDITING
  */
  const confirmPolygon = ({ path, ar, clickFrom = 'QB_MainButton' }) => {
    const { core: { LatLngBounds } } = libraries;

    if (device === 'desktop') {
      createEditMarker();
    }

    // se l'undoMarker è visibile lo cancelliamo
    if (undoMarkerRef.current) {
      undoMarkerRef.current.setMap(null);
      undoMarkerRef.current = null;
    }

    // cancelliamo i vertici di editing
    if (customVertexes.current) {
      customVertexes.current.forEach(x => x.setMap(null));
      customVertexes.current = null;
    }
    
    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }

    // aggiorniamo lo stile della polyline
    polylineRef.current.setOptions({
      ...polylineStyle.complete,
    });

    polygonRef.current.setOptions({
      ...polygonStyle.complete,
    });

    // usciamo dalla fase di editing
    // setIsDrawingComplete(true);

    const newDrawingData = {
      searchAddress: null,
      availableResults: ar,
      action: DRAWING_ACTIONS_MAP.polygon,
      polygon: path,
      activeShapes: [
        polylineRef.current,
        polygonRef.current,
        deleteMarkerRef.current,
        editMarkerRef.current,
      ],
      shapeBounds: getPolygonBounds([polygonRef.current], LatLngBounds),
      from: DRAWING_ACTIONS_MAP.polygon,      
    };

    setDrawingData(newDrawingData);

    newDrawingData.mapBoundsOnCreateShape = mapBoundsOnCreateShape.current;
    if (filtersRef.current.q) {
      newDrawingData.q = filtersRef.current.q;
    }

    setGeoFilters({
      newfilter: {
        geopolygon: { 
          polygon: path,
        },
        q: null,
        geobounds: null,
        geocircle: null,
        nearby: null,
        qExpansion: null,
        ...filtersToAddToQuery.current,
      },
      resetLocations: true,      
      action: 'draw',
      drawingData: newDrawingData,
    });

    if (showYouGotAwayMsg) {
      setShowYouGotAwayMsg(false);
    }

    sleep(150).then(() => {
      trackGTMEvents({
        category: 'Interaction',
        action: 'SeeSearchResults',
        // searchType: 'geopolygon',
        position: clickFrom,
      }, {}, true);
      setSearchMode(null);
    });
  };  

  // aggiungiamo i filtri settati via QB alla query
  const updateFiltersToAddToMapQuery = ({ filtersQB, ar }) => {
    filtersToAddToQuery.current = { ...filtersToAddToQuery.current, ...filtersQB };
    setAvailableResults(ar);
    confirmPolygon({ path: polygonRef.current.getPath(), ar, clickFrom: 'QB_MainButton' });
  };

  const revertPolygon = () => {
    const {
      core: { LatLng },
      maps: { Polyline, Polygon },
    } = libraries;

    const hasShape = polylineRef.current.getPath().getArray().length > 0;
    const currentPath = drawingData.polygon.map(node => new LatLng({ lat: node[0], lng: node[1] }));

    if (!hasShape) {
      polylineRef.current = new Polyline(polylineStyle.complete);
      polylineRef.current.setPath(currentPath);
      polylineRef.current.setMap(map);
  
      polygonRef.current = new Polygon(polygonStyle.complete);
      polygonRef.current.setPath(currentPath);
      polygonRef.current.setMap(map);
    } else {
      polylineRef.current.setOptions({ ...polylineStyle.complete });
      polylineRef.current.setPath(currentPath);
      polygonRef.current.setPath(currentPath);
    }

    const dCoords = getDeleteMarkerCoords({ path: currentPath });
    if (!deleteMarkerRef.current) {
      createDeleteMarker({ coords: dCoords });
    } else {
      deleteMarkerRef.current.position = { lat: dCoords.lat(), lng: dCoords.lng() };
    }

    if (device === 'desktop' && !editMarkerRef.current) {
      createEditMarker();
    }
    
    // se l'undoMarker è visibile lo cancelliamo
    if (undoMarkerRef.current) {
      undoMarkerRef.current.setMap(null);
      undoMarkerRef.current = null;
    }

    // cancelliamo i vertici di editing
    if (customVertexes.current) {
      customVertexes.current.forEach(x => x.setMap(null));
      customVertexes.current = null;
    }
    
    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }

    setIsDrawing(false);
    setHasDrawing(true);
    setSearchMode(null);
    setDrawingData({
      ...drawingData,
      activeShapes: [
        polylineRef.current,
        polygonRef.current,
        deleteMarkerRef.current,
        editMarkerRef.current,
      ],
    });
  };

  /*
    CHIUSURA POLIGONO
  */
  const closePolygon = (fromDblClick = false) => {
    const { core: { LatLngBounds }, geometry: { spherical } } = libraries;

    // se l'utente clicca sullo startDrawingMarker crea un vertice di troppo. questo perchè, per fare in modo che l'ultimo vertice coincida esattamente col primo, 
    // siamo noi a settarlo assegnandogli la posizione del primo. se l'utente chiude il poligono con un doppio click, i vertici di troppo sono due (uno per click).
    // per prima cosa quindi eliminiamo i vertici di troppo
    const vertexesToRemove = fromDblClick ? 2 : 1;

    // non vogliamo chiudere la figura se ha meno di 2 lati (o tre vertici) quindi:

    // 1 - cloniamo l'array dei vertici
    const originalPath = [...polylineRef.current.getPath().getArray()];

    // 2 - rimuoviamo i vertici di troppo dal path
    for (let x = 0; x < vertexesToRemove; x += 1) {
      polylineRef.current.getPath().removeAt(polylineRef.current.getPath().length - 1);
    }

    // 3 - controlliamo se il path risultante ha meno di tre vertici 
    if (polylineRef.current.getPath().length < 3) {
      // nel caso risettiamo il path originale e ci fermiamo qui
      polylineRef.current.setPath(originalPath);
      return;
    }

    // prendiamo l'array dei vertici della figura disegnata
    const path = polylineRef.current.getPath().getArray();
    // e settiamo l'ultimo vertice in modo coincida col primo
    path.push(polylineRef.current.getPath().getArray()[0]);        

    // a metà strada tra i vertici settati dall'utente (vertici principali), bisogna inserire altri vertici che permettano di modificare
    // la figura disegnata (vertici secondari)
    const updatedPath = [...path];
    // per ognuno dei vertici
    for (let x = 0; x < path.length - 1; x += 1) {
      // prendiamo la sua posizione
      const currentVertex = polylineRef.current.getPath().getAt(x);
      // prendiamo la posizione del vertice che lo segue
      const nextVertex = polylineRef.current.getPath().getAt(x + 1);

      // calcoliamo la posizione del punto che sta a metà strada tra i due
      const addedVertexCoords = spherical.computeOffset(currentVertex,
        spherical.computeDistanceBetween(currentVertex, nextVertex) / 2,
        spherical.computeHeading(currentVertex, nextVertex));

      // aggiungiamo il vertice ad un clone dell'array dei vertici della figura disegnata
      updatedPath.splice(x + (x + 1), 0, addedVertexCoords);
    }

    // aggiorniamo path e style del poligono e della polyline
    polygonRef.current.setOptions({ paths: updatedPath });

    polylineRef.current.setOptions({
      path: updatedPath,
      ...polylineStyle.editing,
    });

    // individuiamo il vertice più a nord della figura
    northernMostVertexRef.current = getHighestPoint(polylineRef.current);
    
    // stampiamo i marker che utilizzeremo come vertici custom
    setVertexes({ path: updatedPath });

    const bounds = getPolygonBounds([polygonRef.current], LatLngBounds);
    createCenterMsgMarker({
      polygonCenter: bounds.getCenter(),
      animated: true,
    });

    mapBoundsOnCreateShape.current = map.getBounds();
    map.fitBounds(getPolygonBounds([polygonRef.current], LatLngBounds));

    // dichiariamo che la fase di disegno è conclusa
    setIsDrawing(false);
    setHasDrawing(true);

    // eliminiamo lo startDrawingMarker
    if (startDrawingMarker.current) {
      startDrawingMarker.current.setMap(null);
      startDrawingMarker.current = null;
    }

    fetchAR();
  };

  const createPolygonFromGotAwayMsg = ({ path }) => {
    const {
      core: { LatLng, LatLngBounds },
      maps: { Polyline, Polygon },
    } = libraries;

    const latLngPath = path.map(node => new LatLng({ lat: node[0], lng: node[1] }));

    polylineRef.current = new Polyline(polylineStyle.complete);
    polylineRef.current.setPath(latLngPath);
    polylineRef.current.setMap(map);

    polygonRef.current = new Polygon(polygonStyle.complete);
    polygonRef.current.setPath(latLngPath);
    polygonRef.current.setMap(map);

    const dCoords = getDeleteMarkerCoords({ path: latLngPath });
    createDeleteMarker({ coords: dCoords });

    if (device === 'desktop') {
      createEditMarker();
    }

    updateAvailableResults({ value: 0, loading: true });
    const polygonBounds = getPolygonBounds([polygonRef.current], LatLngBounds);

    const activeShapes = [
      polylineRef.current,
      polygonRef.current,
      deleteMarkerRef.current,
      editMarkerRef.current,
    ];

    const newDrawingData = {
      ...drawingData,
      fromSSearch: false,
      activeShapesToRemove: null,
      activeShapes,
      shapeBounds: polygonBounds,
      from: DRAWING_ACTIONS_MAP.polygon,
    };

    setDrawingData(newDrawingData);

    setGeoFilters({
      newfilter: {
        geopolygon: { 
          polygon: path,
        },
        q: null,
        geobounds: null,
        geocircle: null,
        nearby: null,
        ...filtersToAddToQuery.current,
      },
      resetLocations: true,
    });

    createdFromGAM.current = true;
    map.fitBounds(polygonBounds);
  };

  const createInitialPolygon = ({ path, fromSSearch = false, fromBack = false }) => {
    const {
      core: { LatLng, LatLngBounds },
      maps: { Polyline, Polygon },
    } = libraries;

    // valorizziamo i ref di polyline e polygon
    // in fase di disegno la polyline è la traccia che l'utente costruisce muovendo il mouse e cliccando per aggiungere i vertici del poligono di ricerca,
    // mentre il poligono è la preview dell'area di ricerca che l'utente sta selezionando

    const latLngPath = path.map(node => new LatLng({ lat: node[0], lng: node[1] }));

    if (fromSSearch) {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;

        polygonRef.current.setMap(null);
        polygonRef.current = null;

        deleteMarkerRef.current.setMap(null);
        deleteMarkerRef.current = null;
        editMarkerRef.current.setMap(null);
        editMarkerRef.current = null;
      }
      setSaveSearchDrawingData(null);
      createdFromSSearch.current = false;
    }

    polylineRef.current = new Polyline(polylineStyle.complete);
    polylineRef.current.setPath(latLngPath);
    polylineRef.current.setMap(map);

    polygonRef.current = new Polygon(polygonStyle.complete);
    polygonRef.current.setPath(latLngPath);
    polygonRef.current.setMap(map);

    const dCoords = getDeleteMarkerCoords({ path: latLngPath });
    createDeleteMarker({ coords: dCoords });

    if (device === 'desktop') {
      createEditMarker();
    }

    const polygonBounds = getPolygonBounds([polygonRef.current], LatLngBounds);

    const activeShapes = [
      polylineRef.current,
      polygonRef.current,
      deleteMarkerRef.current,
      editMarkerRef.current,
    ];

    const newDrawingData = {
      ...drawingData,
      fromSSearch: false,
      activeShapesToRemove: null,
      activeShapes,
      shapeBounds: polygonBounds,
      from: DRAWING_ACTIONS_MAP.polygon,
    };

    setDrawingData(newDrawingData);

    if (fromBack) {
      synchPreviousStepsActiveShape(activeShapes);
      setGeoFilters({
        newfilter: {
          geopolygon: { 
            polygon: path,
          },
          q: null,
          geobounds: null,
          geocircle: null,
          nearby: null,
          ...filtersToAddToQuery.current,
        },
        resetLocations: true,
      });
      // setBoundsChangedOnBack(true);
      map.fitBounds(polygonBounds);
      return;
    } else {
      setInitialShapeForPrevious(newDrawingData);
    }

    // centriamo la mappa sul poligono
    if (drawingData?.from === DRAWING_ACTIONS_MAP.polygon) {
      return;
    }

    map.fitBounds(polygonBounds);
  };

  /*
    METODI ASSOCIATI AGLI EVENT LISTENERS in fase di disegno

    sono tutti wrappati da useCallback che hanno come unica dipendenza map
    questo ci assicura che a prescindere dal lifecycle del componente funzionale, gli eventi useranno sempre la stessa versione del metodo visto che la mappa, una volta istanziata non cambia.
    Se non venisse usato questo approccio, il removeListener scatenato quando isDrawing viene settato a false rimuoverebbe una versione differente del metodo e sarebbe pertanto inutile
  */

  /* 
    CLICK 
    crea un nuovo vertice del poligono
  */
  
  const clickListener = useCallback((e) => {
    const { marker: { AdvancedMarkerElement } } = libraries;    
    // se l'utente ha settato il primo vertice creiamo un marker cliccabile per aiutarlo a chiudere correttamente il poligono
    if (!hasDraggedWhileDrawing.current) {
      if (!startDrawingMarker.current) {
        startDrawingMarker.current = new AdvancedMarkerElement({
          map,
          position: {
            lat: mouseCoordsToLatLng(e, map, device, isSafari).lat(),
            lng: mouseCoordsToLatLng(e, map, device, isSafari).lng(),
          },
          content: setMarkerContent({ start: true }),
        });
        // utilizziamo l'addEventListener degli elementi DOM standard per utilizzare preventDefault e stopPropagation
        // e annullare qualsiasi azione che non sia la nostra closePolygon compresa l'aggiunta di un vertice al click
        // che avviene nelle righe seguenti di questo metodo
        startDrawingMarker.current.addEventListener('click', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          closePolygon();
        });
      } 
      // aggiungiamo un vertice al path della polyline laddove l'utente ha cliccato
      const v = mouseCoordsToLatLng(e, map, device, isSafari);
      polylineRef.current.getPath().push(v);
    } else {
      hasDraggedWhileDrawing.current = false; 
    }
  }, [map]);

  const dragListener = useCallback(() => {
    hasDraggedWhileDrawing.current = true;
  }, [map]);

  /*
    MOUSEMOVE
    mostra all'utente una preview del poligono che sta creando
  */
  const moveListener = useCallback((e) => {
    const path = polylineRef.current.getPath();    
    // se l'utente ha già creato almeno un vertice
    if (path.length) {
      // dobbiamo aggiungere un vertice al path del poligono per creare la preview del poligono
      // al primo mousemove dopo il click che ha creato il primo vertice, la figura ha un solo vertice: non dobbiamo sovrascriverlo, pertanto inseriamo il secondo vertice all'indice 1 dell'array
      const index = path.length - 1 <= 0 ? 1 : path.length - 1; 
      // inseriamo il nuovo vertice nell'array dei vertici della figura all'indice appena calcolato e con le coordinate latLng calcolate a partire dalla posizione del mouse sul div che contiene la mappa
      path.setAt(index, mouseCoordsToLatLng(e, map, device, isSafari));
      // aggiorniamo il path della polyline e del poligono per creare l'effetto preview
      polylineRef.current.setPath(path);
      polygonRef.current.setPath(path);
    }
  }, [map]);

  /*
    DOUBLE CLICK
    chiude il poligono senza bisogno di cliccare sul primo vertice settato
  */
  const dblClickListener = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (startDrawingMarker.current && startDrawingMarker.current.content && e.target === startDrawingMarker.current.content.firstChild) {
      return;
    }
    closePolygon(true);
  }, [map]);

  /* 
    TOUCH START
    crea il poligono
  */

  // è necessario usare un debouce del metodo in modo da prevenire un'incorretta detection di interazioni a un o due dita:
  // l'utente potrebbe voler zoomare con uno swipe a due dita, ma mettere prima un dito, poi l'altro sullo schermo
  const touchStartListenerDebounced = debounce((e) => {
    // se l'utente fa uno swipe a un dito
    if (e.touches.length === 1) {
      // abilitiamo il disegno
      isMouseDown.current = true;
      map.setOptions({ gestureHandling: 'none' });
    }
    // altrimenti sappiamo che sta zoomando o facendo pan sulla mappa
  }, 25);

  const touchStartListener = useCallback(e => touchStartListenerDebounced(e), [map]);

  /* 
    TOUCH MOVE 
    disegna il poligono
  */

  // usiamo un debouce sul touchmove per evitare che un movimento troppo veloce crei poligoni sghembi
  const touchMoveListenerDebounced = debounce((e) => {
    // se il touchstart non è stato valido o non riceviamo info sull'evento
    // risettiamo tutto ai valori di default e usciamo senza fare niente
    if (!isMouseDown.current || !e) {
      isMouseDown.current = false;
      map.setOptions({ gestureHandling: 'greedy' });
      return;
    }

    // altrimenti ricaviamo un oggetto latLng dalle coordinate della gesture
    const v = mouseCoordsToLatLng(e, map, device, isSafari);
    const path = polylineRef.current.getPath().getArray();
    // aggiungiamo il nuovo oggetto latLng al path della polyline
    path.push(v);
    // aggiorniamo la polyline
    polylineRef.current.setPath(path);
  }, 5);

  const touchMoveListener = useCallback(e => touchMoveListenerDebounced(e), [map, device, isSafari]);

  /* 
    TOUCH END
    completa il poligono
  */
  const touchEndListener = useCallback(() => {
    const {
      core: { LatLng, LatLngBounds },
      geometry: { spherical },
    } = libraries;

    // recuperiamo tutti i vertici che compongono la polyline
    const path = polylineRef.current.getPath().getArray();

    // se l'utente non ha mai iniziato a disegnare (ad esempio ha fatto swipe con 2 dita o ha tappato senza fare swipe)
    if (!isMouseDown.current || !path.length) {
      // non bisogna fare niente
      return;
    }

    // altrimenti, chiudiamo la polyline aggiungendo un ultimo vertice le cui coordinate corrispondono a quelle del primo
    path.push(polylineRef.current.getPath().getArray()[0]);

    /* semplifichiamo il poligono disegnato - start */

    // inseriamo le coordinate di tutti i vertici in un array
    const coords = path.map((x) => ({ x: x.lat(), y: x.lng() }));

    // recuperiamo il valore di zoom della mappa
    const zoom = map.getZoom();

    // mappatura statica zoom/ratio di semplificazione
    let tolerance = 0.001;
    if (zoom < 11) {
      tolerance = 0.01;
    } else if (zoom > 13 && zoom < 17) {
      tolerance = 0.0001;
    } else if (zoom >= 17) {
      tolerance = 0.00001;
    }

    // usiamo la libreria di semplificazione
    const simplified = simplify(coords, tolerance);

    // convertiamo i punti validi dopo la semplificazione in vertici validi per le shapes di gmaps
    const correctPath = simplified.map((obj) => ({ lat: obj.x, lng: obj.y }));

    /* semplifichiamo il poligono disegnato - end */

    // se il path ha un numero pari di vertici, abbiamo un problema:
    // nel caso in cui (su desktop) l'utente voglia editare il poligono
    // abbiamo bisogno di un poligono con un numero di vertici dispari per fare
    // il gioco vertice principale/vertice secondario... quindi
    if (correctPath.length % 2 === 0) {
      // prendiamo la posizione del penultimo vertice
      const currentVertex = new LatLng(correctPath[correctPath.length - 2]);
      // prendiamo la posizione dell'ultimo vertice
      const nextVertex = new LatLng(correctPath[correctPath.length - 1]);
      // calcoliamo la metà della distanza che li separa
      const addedVertexCoords = spherical.computeOffset(currentVertex,
        spherical.computeDistanceBetween(currentVertex, nextVertex) / 2,
        spherical.computeHeading(currentVertex, nextVertex));
      // e aggiungiamo un vertice
      correctPath.splice(correctPath.length - 1, 0, addedVertexCoords);
    }

    // aggiorniamo la polyline
    polylineRef.current.setOptions({
      path: correctPath,
      ...polylineStyle.complete,
    });

    // passiamo il path al poligono
    polygonRef.current.setOptions({ paths: correctPath });

    const dCoords = new LatLng(getDeleteMarkerCoords({ path: correctPath }));
    createDeleteMarker({ coords: dCoords });

    const bounds = getPolygonBounds([polygonRef.current], LatLngBounds);
    createCenterMsgMarker({
      polygonCenter: bounds.getCenter(),
      animated: true,
    });

    mapBoundsOnCreateShape.current = map.getBounds();
    // centriamo la mappa sul poligono
    map.fitBounds(bounds);

    // usciamo dalla modalità di disegno
    isMouseDown.current = false;
    setHasDrawing(true);
    setIsDrawing(false);

    // Validazione geometria: verifica che tutti i lati e gli angoli siano > 3
    const geometryValidation = validatePolygonGeometry(correctPath);
    fetchAR(geometryValidation.isValid);

    map.setOptions({ gestureHandling: 'greedy' });
  }, [map]);

  useEffect(() => {
    // quando l'utente inizia a disegnare
    if (initialized) {
      if (isDrawing) {      
        const { maps: { Polyline, Polygon } } = libraries;
        // valorizziamo i ref di polyline e polygon
        // in fase di disegno la polyline è la traccia che l'utente costruisce muovendo il mouse e cliccando per aggiungere i vertici del poligono di ricerca,
        // mentre il poligono è la preview dell'area di ricerca che l'utente sta selezionando
        polylineRef.current = new Polyline(polylineStyle.drawing);
        polylineRef.current.setPath([]);
        polylineRef.current.setMap(map);
  
        polygonRef.current = new Polygon(polygonStyle.drawing);
        polygonRef.current.setPath([]);
        polygonRef.current.setMap(map); 

        // attacchiamo gli event listener al div che contiene la mappa in modo da permettere le azioni di disegno
        if (device === 'desktop') {
          map.setOptions({ draggableCursor: 'crosshair' });
          map.addListener('dragstart', dragListener);     
          container.current.addEventListener('click', clickListener);
          container.current.addEventListener('mousemove', moveListener);
          container.current.addEventListener('dblclick', dblClickListener);
        } else {          
          container.current.addEventListener('touchstart', touchStartListener);
          container.current.addEventListener('touchmove', touchMoveListener);
          container.current.addEventListener('touchend', touchEndListener);
        }
  
      // una volta che l'utente ha completato il disegno del poligono
      // rimuoviamo gli event listeners
      } else if (device === 'desktop') {        
        if (map) {
          const { core: { event: { clearListeners } } } = libraries;
          map.setOptions({ draggableCursor: null });
          clearListeners(map, 'dragstart');
        }        
        if (container.current) {
          container.current.removeEventListener('click', clickListener); 
          container.current.removeEventListener('mousemove', moveListener);
          container.current.removeEventListener('dblclick', dblClickListener);
        }
      } else {
        if (container.current) {
          container.current.removeEventListener('touchstart', touchStartListener);
          container.current.removeEventListener('touchmove', touchMoveListener);
          container.current.removeEventListener('touchend', touchEndListener);
        }
      }
    }
    return () => {
      // rimuoviamo gli event listeners
      if (device === 'desktop') {
        if (map) {
          const { core: { event: { clearListeners } } } = libraries;
          map.setOptions({ draggableCursor: null });
          clearListeners(map, 'dragstart');
        }
        if (container.current) {
          container.current.removeEventListener('click', clickListener); 
          container.current.removeEventListener('mousemove', moveListener);
          container.current.removeEventListener('dblclick', dblClickListener);
        }
      } else {
        if (container.current) {
          container.current.removeEventListener('touchstart', touchStartListener);
          container.current.removeEventListener('touchmove', touchMoveListener);
          container.current.removeEventListener('touchend', touchEndListener);
        }
      }
    };
  }, [
    libraries,
    map,
    container,
    isDrawing,
    device,
    initialized,
  ]);

  // creo il cerchio a partire dai parametri della url
  useEffect(() => {
    if (initialized && pathFromInit) {
      if (drawingData && drawingData.from === 'got-away-msg') {
        return;
      }
      createInitialPolygon({
        initial: true,
        path: pathFromInit,
        fromBack: drawingData?.fromBack,
      });
    }
  }, [initialized, pathFromInit]);

  useEffect(() => {    
    if (drawingData) {
      if (drawingData.action !== DRAWING_ACTIONS_MAP.polygon && (drawingData.activeShapes || []).length) {
        removeActiveShapes({ drawingData });
      } else if (drawingData.action === DRAWING_ACTIONS_MAP.polygon && (drawingData.activeShapesToRemove || []).length > 0 && !createdFromSSearch.current) {
        createdFromSSearch.current = true;
        const fromSSearch = true;
        removeActiveShapes({ drawingData, fromSSearch });
        createInitialPolygon({
          initial: true,
          path: drawingData?.polygon,
          fromSSearch,
        });
      } else if (drawingData.action === DRAWING_ACTIONS_MAP.polygon && drawingData.from === 'got-away-msg' && !createdFromGAM.current) {
        createPolygonFromGotAwayMsg({ path: drawingData?.polygon });
      } else if (drawingData.action === DRAWING_ACTIONS_MAP.polygon && drawingData.availableResults?.value && drawingData.availableResults?.value !== availableResults.value) {
        updateAvailableResults({ ...availableResults, value: drawingData.availableResults?.value || totalListResults });
      }
    }
  }, [drawingData]);

  useEffect(() => {
    if (drawingData?.from === 'got-away-msg' && createdFromGAM.current) {
      if (!isFetching && availableResults.loading) {
        const {
          core: { LatLngBounds },
        } = libraries;

        updateAvailableResults({ value: totalListResults, loading: false, });
        const polygonBounds = getPolygonBounds([polygonRef.current], LatLngBounds);

        const activeShapes = [
          polylineRef.current,
          polygonRef.current,
          deleteMarkerRef.current,
          editMarkerRef.current,
        ];
    
        const newDrawingData = {
          ...drawingData,
          availableResults: {
            value: totalListResults,
            loading: false,
          },
          fromSSearch: false,
          activeShapesToRemove: null,
          activeShapes,
          shapeBounds: polygonBounds,
          from: DRAWING_ACTIONS_MAP.polygon,
        };
    
        setDrawingData(newDrawingData);
      }
      createdFromGAM.current = false;
    } else if (drawingData && drawingData.availableResults && totalListResults) {
      updateAvailableResults({ loading: false, value: totalListResults });
    }
  }, [drawingData, totalListResults, isFetching, libraries]);

  // aggiorno la referenza interna di searchMode
  useEffect(() => {
    searchModeRef.current = searchMode;
    if (searchMode === DRAWING_ACTIONS_MAP.polygon && polylineRef.current?.getPath().length && !startEditingRef.current) { 
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      
      // cancelliamo l'editMarker
      if (editMarkerRef.current) {
        editMarkerRef.current.setMap(null);
        editMarkerRef.current = null;
      }

      // cancelliamo il deleteMarkerRef
      if (deleteMarkerRef.current) {
        deleteMarkerRef.current.setMap(null);
        deleteMarkerRef.current = null;
      }

      setIsDrawing(true);
      setHasDrawing(false);
    } else if (startEditingRef.current) {
      startEditingRef.current = false;
    }
  }, [searchMode]);

  const exitQBDrawing = () => {
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (startDrawingMarker.current) {
      startDrawingMarker.current.setMap(null);
      startDrawingMarker.current = null;
    }
    setIsDrawing(false);
  };

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (deleteDraftAndStopSearchMode) {
      if (drawingData && drawingData.action === DRAWING_ACTIONS_MAP.polygon) {
        revertPolygon();
      } else {
        deleteDraft();
      }
      setSearchMode(null);
      toggleDeleteDraftAndStopSearchMode();
    }
  }, [deleteDraftAndStopSearchMode]);


  const startDrawingPolygon = () => setIsDrawing(true);

  return initialized && (
    <>      
      <Overlayer
        isOpen={!!searchMode}
        columnsAreCollapsed={columnsAreCollapsed}
      >
        <Controls
          map={map}
          searchMode={searchMode}
          setSearchMode={setSearchMode}
          device={device}
          libraries={libraries}
          hasDrawing={hasDrawing}
          availableResults={availableResults}
          availablePoi={availablePoi}
          updateFiltersToAddToMapQuery={updateFiltersToAddToMapQuery}
          confirmPolygon={confirmPolygon}
          revertPolygon={revertPolygon}
          deletePolygon={deletePolygon}
          deleteDraft={deleteDraft}
          polygonPath={polygonPath}
          drawingData={drawingData}
          openQbFiltersPanel={openQbFiltersPanel}
          qbGoToSRP={qbGoToSRP}
          qbSSearchAction={qbSSearchAction}
          controlsVariant={controlsVariant}
          isDrawing={isDrawing}
          startDrawingPolygon={startDrawingPolygon}
          exitQBDrawing={exitQBDrawing}
          polygonIsNotValid={polygonIsNotValidRef.current}
        />
      </Overlayer>
      {(qbGoToSRP !== null && !isDrawing && !hasDrawing) ? (
        <div className="qbmap__polybtn">
          <div role="button" tabIndex="0" className={`qbmap__polybtn__btn in-page-${device} pr--m pl--ms tp-s--l tp-w--l b--s c-txt--w c-bg--primary c-br--primary is-clickable`} onClick={startDrawingPolygon}>
            <Icon aria-hidden="true" name={Draw} className="mr--xs" width="24px" fill />Inizia a disegnare
          </div>
        </div>
      ) : null}
      {(qbGoToSRP !== null && isDrawing && !hasDrawing) ? (
        <div className="qbmap__polybtn">
          <p className="p--xs tp-s--xs c-txt--f0 c-bg--bg">Disegna la tua area</p>
          <div role="button" tabIndex="0" className={`qbmap__polybtn__btn in-page-${device} tp-s--l tp-w--m b--s tp-a--c c-txt--f0 c-bg--w c-br--border is-clickable`} onClick={exitQBDrawing}>
            Annulla
          </div>
        </div>
      ) : null}      
    </>
  );
};

export default PolygonTool;

PolygonTool.propTypes = {
  initialized: PropTypes.bool,
  map: PropTypes.instanceOf(Object),
  container: PropTypes.instanceOf(Object),
  searchMode: PropTypes.string,
  setSearchMode: PropTypes.func,
  device: PropTypes.string,
  libraries: PropTypes.instanceOf(Object),
  drawingData: PropTypes.instanceOf(Object),
  columnsAreCollapsed: PropTypes.bool,
  updateQuery: PropTypes.func,
  getAvailableResults: PropTypes.func,
  setDrawingData: PropTypes.func,
  setGeoFilters: PropTypes.func,
  viewport: PropTypes.instanceOf(Object),
  openQbFiltersPanel: PropTypes.func,
  qbGoToSRP: PropTypes.func,
  qbSSearchAction: PropTypes.func,
  controlsVariant: PropTypes.string,
  setSaveSearchDrawingData: PropTypes.func,
  isSafari: PropTypes.bool,
  showYouGotAwayMsg: PropTypes.bool,
  setShowYouGotAwayMsg: PropTypes.func,
};

PolygonTool.defaultProps = {
  initialized: false,
  map: null,
  container: null,
  searchMode: null,
  setSearchMode: PropTypes.func,
  device: '',
  libraries: null,
  drawingData: null,
  columnsAreCollapsed: false,
  updateQuery: () => {},
  getAvailableResults: () => {},
  setDrawingData: () => {},
  setGeoFilters: () => {},
  viewport: {},
  openQbFiltersPanel: null,
  qbGoToSRP: null,
  qbSSearchAction: null,
  controlsVariant: null,
  setSaveSearchDrawingData: () => {},
  isSafari: false,
  showYouGotAwayMsg: false,
  setShowYouGotAwayMsg: () => {},
};
